// ─── SKU Master Page (Theme-Consistent Professional Design System) ──────────────
import React, { useState, useEffect } from 'react';
import { 
  Hash, Save, Copy, CheckCircle, Settings, Info, RefreshCw, RotateCcw, 
  Search, Edit2, AlertTriangle, ChevronLeft, ChevronRight, X, Layers,
  ShieldCheck, ArrowRight, Zap, Sparkles, Filter, Database
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const skuAPI = {
  get:         ()           => api.get('/api/skumaster'),
  getRegistry: (page, s)    => api.get(`/api/skumaster/registry?page=${page}&pageSize=10${s ? `&search=${encodeURIComponent(s)}` : ''}`),
  updateSKU:   (data)       => api.post('/api/skumaster/update-sku', data),
  save:        (data)       => api.post('/api/skumaster/save', data),
  preview:     (data)       => api.post('/api/skumaster/preview', data),
  generate:    (data)       => api.post('/api/skumaster/generate', data),
  reset:       (seq)        => api.post('/api/skumaster/reset', { seq }),
};

export function SKUMasterPage() {
  // Navigation Tabs: 'registry' (default) | 'config'
  const [activeTab, setActiveTab] = useState('registry');

  // Format config
  const [config, setConfig] = useState({
    companyPrefix: 'CQ',
    separator: '',
    charCount: 2,
    seqPadding: 5,
    currentSeq: 0,
  });

  // Generator inputs & Preview
  const [form] = useState({ itemName: 'RING', size: '14', gender: 'W' });
  const [preview, setPreview]   = useState('CQRG14W00081');
  const [copied, setCopied]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);

  // Registry & Pagination state (10 items per page)
  const [registry, setRegistry]         = useState([]);
  const [regPage, setRegPage]           = useState(1);
  const [regTotalPages, setRegTotalPages] = useState(1);
  const [regTotal, setRegTotal]         = useState(0);
  const [regSearch, setRegSearch]       = useState('');
  const [regLoading, setRegLoading]     = useState(false);

  // Quick edit SKU modal
  const [editModal, setEditModal]       = useState(null);
  const [editSKUForm, setEditSKUForm]   = useState({ skuNo: '', collection: '', productSize: '' });
  const [editSaving, setEditSaving]     = useState(false);

  // Load saved config & dynamic current sequence
  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await skuAPI.get();
      const d = res.data?.data;
      if (d) {
        const cfg = {
          companyPrefix: d.companyPrefix || d.CompanyPrefix || d.companyprefix || 'CQ',
          separator:     d.separator     || d.Separator     || d.separator     || '',
          charCount:     d.charCount     || d.CharCount     || d.charcount     || 2,
          seqPadding:    d.seqPadding    || d.SeqPadding    || d.seqpadding    || 5,
          currentSeq:    d.currentSeq    ?? d.CurrentSeq    ?? d.currentseq    ?? 0,
        };
        setConfig(cfg);
      }
    } catch { }
    finally { setLoading(false); }
  };

  // Load paginated SKU registry
  const loadRegistry = async (page = regPage, search = regSearch) => {
    setRegLoading(true);
    try {
      const res = await skuAPI.getRegistry(page, search);
      const dataObj = res.data?.data || {};
      const list = dataObj.data || dataObj.Data || (Array.isArray(dataObj) ? dataObj : []);
      setRegistry(list);
      setRegTotalPages(dataObj.totalPages || dataObj.TotalPages || 1);
      setRegTotal(dataObj.total || dataObj.Total || list.length);
    } catch {
      toast.error('Failed to load SKU registry');
    } finally {
      setRegLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    loadRegistry(1, '');
  }, []);

  useEffect(() => {
    loadRegistry(regPage, regSearch);
  }, [regPage]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setRegPage(1);
    loadRegistry(1, regSearch);
  };

  // Build preview locally whenever config changes
  useEffect(() => {
    const sep  = config.separator || '';
    const cc   = Number(config.charCount) || 2;
    const pad  = Number(config.seqPadding) || 5;
    const next = String((config.currentSeq || 0) + 1).padStart(pad, '0');
    const parts = [];
    if (config.companyPrefix) parts.push(config.companyPrefix.toUpperCase());
    if (form.itemName)        parts.push(form.itemName.toUpperCase().slice(0, cc));
    if (form.size)            parts.push(form.size);
    if (form.gender)          parts.push(form.gender.slice(0, 1).toUpperCase());
    parts.push(next);
    setPreview(parts.join(sep));
  }, [config, form]);

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (!config.companyPrefix.trim()) return toast.error('Company Prefix is required');
    setSaving(true);
    try {
      await skuAPI.save({
        companyPrefix: config.companyPrefix.trim().toUpperCase(),
        separator:     config.separator,
        charCount:     Number(config.charCount),
        seqPadding:    Number(config.seqPadding),
        currentSeq:    Number(config.currentSeq || 0),
      });
      toast.success('SKU format saved successfully!');
      loadConfig();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleResetToZero = async () => {
    if (!window.confirm('Reset SKU sequence counter back to 0? (Next generated SKU will start from 00001)')) return;
    try {
      await skuAPI.reset(0);
      toast.success('SKU sequence counter reset to 0!');
      loadConfig();
      loadRegistry(1, regSearch);
    } catch { toast.error('Failed to reset sequence'); }
  };

  const copyPreview = () => {
    navigator.clipboard.writeText(preview).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('SKU copied to clipboard!');
    });
  };

  // Open Edit SKU Modal
  const openEditSKU = (row) => {
    setEditModal(row);
    setEditSKUForm({
      skuNo: row.skuNo || row.SKUNo || row.skuno || '',
      collection: row.collection || row.Collection || row.collectionname || '',
      productSize: row.productSize || row.ProductSize || row.productsize || '',
    });
  };

  // Save Edited SKU
  const handleSaveEditedSKU = async (e) => {
    e.preventDefault();
    if (!editSKUForm.skuNo.trim()) {
      toast.error('SKU Number is required');
      return;
    }
    setEditSaving(true);
    try {
      await skuAPI.updateSKU({
        purchaseDetailID: editModal.purchaseDetailID || editModal.PurchaseDetailID || editModal.purchasedetailid || null,
        skuNo: editSKUForm.skuNo.trim().toUpperCase(),
        collection: editSKUForm.collection || null,
        productSize: editSKUForm.productSize || null,
      });
      toast.success(`SKU updated to ${editSKUForm.skuNo.trim().toUpperCase()}`);
      setEditModal(null);
      loadConfig();
      loadRegistry(regPage, regSearch);
    } catch (err) {
      const rawMsg = err.response?.data?.message || err.message || '';
      const cleanMsg = rawMsg
        .replace(/^Error updating SKU:\s*/i, '')
        .replace(/\d+:\s*syntax error at or near\s*/i, 'Syntax error near ')
        .replace(/POSITION:\s*\d+/gi, '')
        .trim();
      toast.error(cleanMsg || 'Failed to update SKU');
    } finally {
      setEditSaving(false);
    }
  };

  // Build segments for Live Preview
  const buildSegments = () => {
    const sep  = config.separator || '';
    const cc   = Number(config.charCount) || 2;
    const pad  = Number(config.seqPadding) || 5;
    const next = String((config.currentSeq || 0) + 1).padStart(pad, '0');
    const segs = [];
    if (config.companyPrefix) segs.push({ text: config.companyPrefix.toUpperCase(), label: 'Prefix' });
    if (sep && segs.length > 0) segs.push({ text: sep, label: '', sep: true });
    segs.push({ text: form.itemName.slice(0, cc), label: 'Item' });
    if (sep) segs.push({ text: sep, label: '', sep: true });
    segs.push({ text: form.size, label: 'Size' });
    segs.push({ text: form.gender, label: 'Gender' });
    if (sep) segs.push({ text: sep, label: '', sep: true });
    segs.push({ text: next, label: 'Auto Seq', isSeq: true });
    return segs;
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header Banner */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 24, flexWrap: 'wrap', gap: 14
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, #0d1f35 0%, #1e3e66 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(13,31,53,0.35)',
          }}>
            <Hash size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.45rem', fontWeight: 800, color: 'var(--text-primary)' }}>
              SKU Master & Registry
            </h1>
            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Configure SKU formats, monitor sequence continuity, and edit SKU numbers
            </p>
          </div>
        </div>

        <button
          onClick={() => { loadConfig(); loadRegistry(regPage, regSearch); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 16px', borderRadius: 8, fontSize: '0.82rem',
            fontWeight: 700, cursor: 'pointer', border: '1px solid var(--border)',
            background: '#ffffff', color: '#153054',
            boxShadow: '0 2px 4px rgba(0,0,0,0.03)', transition: 'all 0.15s'
          }}>
          <RefreshCw size={14} color="#153054" /> Refresh Data
        </button>
      </div>

      {/* App Theme Executive Stats Bar */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16, marginBottom: 24
      }}>
        {/* Card 1: Total SKUs */}
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14,
          padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          display: 'flex', alignItems: 'center', gap: 14
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 10, background: '#f0f5fd', border: '1px solid #d0e1f9',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Database size={20} color="#153054" />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Total Registered SKUs
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#153054' }}>
              {regTotal} Items
            </div>
          </div>
        </div>

        {/* Card 2: Current Dynamic Counter */}
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14,
          padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          display: 'flex', alignItems: 'center', gap: 14
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 10, background: '#ecfdf5', border: '1px solid #a7f3d0',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Zap size={20} color="#059669" />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Current Dynamic Counter
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#059669', fontFamily: "'JetBrains Mono', monospace" }}>
              #{config.currentSeq} <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>(Next: #{config.currentSeq + 1})</span>
            </div>
          </div>
        </div>

        {/* Card 3: Next Live SKU Preview */}
        <div style={{
          background: 'linear-gradient(135deg, #0b1829 0%, #153054 100%)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 14,
          padding: '16px 20px', boxShadow: '0 8px 20px -4px rgba(11,24,41,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#ffffff'
        }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Next Live SKU Preview
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#f59e0b', fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
              {preview}
            </div>
          </div>
          <button
            onClick={copyPreview}
            style={{
              background: copied ? '#10b981' : 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#fff',
              transition: 'all 0.2s'
            }}
            title="Copy SKU"
          >
            {copied ? <CheckCircle size={17} /> : <Copy size={17} />}
          </button>
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 20, borderBottom: '2px solid var(--border)', paddingBottom: 2
      }}>
        <button
          onClick={() => setActiveTab('registry')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            borderRadius: '8px 8px 0 0', fontSize: '0.9rem', fontWeight: 800,
            cursor: 'pointer', border: 'none', transition: 'all 0.2s',
            background: activeTab === 'registry' ? 'var(--card-bg)' : 'transparent',
            color: activeTab === 'registry' ? '#153054' : 'var(--text-muted)',
            borderBottom: activeTab === 'registry' ? '3px solid #153054' : '3px solid transparent',
          }}>
          <Database size={16} /> SKU Sequence Registry ({regTotal})
        </button>

        <button
          onClick={() => setActiveTab('config')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            borderRadius: '8px 8px 0 0', fontSize: '0.9rem', fontWeight: 800,
            cursor: 'pointer', border: 'none', transition: 'all 0.2s',
            background: activeTab === 'config' ? 'var(--card-bg)' : 'transparent',
            color: activeTab === 'config' ? '#153054' : 'var(--text-muted)',
            borderBottom: activeTab === 'config' ? '3px solid #153054' : '3px solid transparent',
          }}>
          <Settings size={16} /> Format Rules & Counter Settings
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════ */}
      {/* TAB 1: SKU SEQUENCE REGISTRY TABLE                       */}
      {/* ════════════════════════════════════════════════════════ */}
      {activeTab === 'registry' && (
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: 16, padding: 24, boxShadow: '0 4px 20px -2px rgba(0,0,0,0.04)'
        }}>
          {/* Table Toolbar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 20, flexWrap: 'wrap', gap: 12
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10 }}>
                Registered Item SKUs
                <span style={{ fontSize: '0.78rem', background: '#f0f5fd', color: '#153054', border: '1px solid #d0e1f9', fontWeight: 800, padding: '2px 10px', borderRadius: 20 }}>
                  Showing {registry.length} of {regTotal} SKUs
                </span>
              </h2>
              <p style={{ margin: '3px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Sorted in descending sequence order (newest on top). Click "Edit SKU" to fix any sequence gap or number typo.
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 8 }}>
              <div style={{ position: 'relative' }}>
                <Search size={15} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  placeholder="Search by SKU, Item, Supplier..."
                  style={{
                    paddingLeft: 34, paddingRight: 30, height: 36, fontSize: '0.84rem', width: 250,
                    borderRadius: 8, border: '1.5px solid var(--border)', outline: 'none'
                  }}
                  value={regSearch}
                  onChange={e => setRegSearch(e.target.value)}
                />
                {regSearch && (
                  <button
                    type="button"
                    onClick={() => { setRegSearch(''); setRegPage(1); loadRegistry(1, ''); }}
                    style={{ position: 'absolute', right: 8, top: 9, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <button
                type="submit"
                style={{
                  background: 'linear-gradient(135deg, #0d1f35 0%, #1e3e66 100%)',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '0 16px', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(13,31,53,0.3)'
                }}>
                Search
              </button>
            </form>
          </div>

          {/* SKU Data Table */}
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: 800 }}>SKU Tag & Sequence</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: 800 }}>Item & Category</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: 800 }}>Spec & Collection</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: 800 }}>Invoice / Supplier</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: 800, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {regLoading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontWeight: 600 }}>
                      <RefreshCw size={20} className="spin" style={{ marginBottom: 8 }} />
                      <div>Loading SKU Registry...</div>
                    </td>
                  </tr>
                ) : registry.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontWeight: 600 }}>
                      No SKUs found matching search criteria
                    </td>
                  </tr>
                ) : (
                  registry.map((r, idx) => {
                    const skuNo        = r.skuNo || r.SKUNo || r.skuno || '';
                    const seqNum       = r.seqNum ?? r.SeqNum ?? r.seqnum ?? 0;
                    const itemName     = r.itemName || r.ItemName || r.itemname || r.categoryName || r.CategoryName || 'Item';
                    const categoryName = r.categoryName || r.CategoryName || '';
                    const collection   = r.collection || r.Collection || r.collectionname || '';
                    const productSize  = r.productSize || r.ProductSize || r.productsize || '';
                    const purchaseNo   = r.purchaseNo || r.PurchaseNo || r.purchaseno || '';
                    const supplierName = r.supplierName || r.SupplierName || r.suppliername || '';
                    const hasGapAfter  = r.hasGapAfter || r.HasGapAfter || false;
                    const detailId     = r.purchaseDetailID || r.PurchaseDetailID || r.purchasedetailid || idx;

                    return (
                      <React.Fragment key={detailId}>
                        <tr style={{
                          borderBottom: '1px solid var(--border)',
                          background: hasGapAfter ? '#fff5f5' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                          transition: 'background 0.15s'
                        }}>
                          {/* Col 1: SKU Tag & Sequence */}
                          <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{
                                fontFamily: "'JetBrains Mono', monospace", fontSize: '0.95rem', fontWeight: 800,
                                background: '#f0f5fd', color: '#153054', border: '1px solid #d0e1f9',
                                padding: '4px 10px', borderRadius: 6
                              }}>
                                {skuNo || '—'}
                              </span>
                              <span style={{
                                fontSize: '0.78rem', fontWeight: 800, color: '#475569',
                                background: '#f1f5f9', border: '1px solid var(--border)', padding: '3px 8px', borderRadius: 6
                              }}>
                                #{seqNum}
                              </span>
                            </div>
                          </td>

                          {/* Col 2: Item & Category */}
                          <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                            <div style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                              {itemName}
                            </div>
                            {categoryName && (
                              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 2, fontWeight: 600 }}>
                                Category: {categoryName}
                              </div>
                            )}
                          </td>

                          {/* Col 3: Spec & Collection */}
                          <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              {collection && (
                                <span style={{
                                  fontSize: '0.75rem', fontWeight: 800, background: '#f0fdf4',
                                  color: '#15803d', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: 6
                                }}>
                                  {collection}
                                </span>
                              )}
                              {productSize && (
                                <span style={{
                                  fontSize: '0.75rem', fontWeight: 800, background: '#fef3c7',
                                  color: '#b45309', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: 6
                                }}>
                                  Size: {productSize}
                                </span>
                              )}
                              {!collection && !productSize && <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>}
                            </div>
                          </td>

                          {/* Col 4: Invoice & Supplier */}
                          <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                            <div style={{ fontWeight: 700, color: '#153054' }}>
                              {purchaseNo ? `Inv #${purchaseNo}` : 'Direct Stock'}
                            </div>
                            {supplierName && (
                              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 2 }}>
                                Supplier: {supplierName}
                              </div>
                            )}
                          </td>

                          {/* Col 5: Action Button */}
                          <td style={{ padding: '12px 16px', textAlign: 'right', verticalAlign: 'middle' }}>
                            <button
                              onClick={() => openEditSKU(r)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '6px 12px', borderRadius: 8, fontSize: '0.8rem',
                                fontWeight: 800, cursor: 'pointer', border: '1px solid #d0e1f9',
                                background: '#f0f5fd', color: '#153054',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.03)', transition: 'all 0.15s'
                              }}>
                              <Edit2 size={13} /> Edit SKU
                            </button>
                          </td>
                        </tr>

                        {/* Gap Alert Banner */}
                        {hasGapAfter && (
                          <tr>
                            <td colSpan={5} style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca', padding: '8px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#991b1b', fontSize: '0.82rem', fontWeight: 800 }}>
                                <AlertTriangle size={15} color="#dc2626" />
                                <span>⚠️ Sequence gap detected between #{seqNum} and lower SKU! Click "Edit SKU" to correct sequence jump.</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls (10 items per page) */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 18, flexWrap: 'wrap', gap: 12
          }}>
            <div style={{ fontSize: '0.84rem', color: 'var(--text-muted)', fontWeight: 700 }}>
              Showing Page <strong style={{ color: 'var(--text-primary)' }}>{regPage}</strong> of <strong style={{ color: 'var(--text-primary)' }}>{regTotalPages}</strong> (10 SKUs per page)
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                disabled={regPage <= 1}
                onClick={() => setRegPage(p => Math.max(1, p - 1))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8,
                  fontSize: '0.84rem', fontWeight: 800, cursor: regPage <= 1 ? 'not-allowed' : 'pointer',
                  border: '1px solid var(--border)', background: regPage <= 1 ? '#f1f5f9' : '#ffffff',
                  color: regPage <= 1 ? '#94a3b8' : '#153054', boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                }}>
                <ChevronLeft size={16} /> Previous
              </button>
              <button
                disabled={regPage >= regTotalPages}
                onClick={() => setRegPage(p => Math.min(regTotalPages, p + 1))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8,
                  fontSize: '0.84rem', fontWeight: 800, cursor: regPage >= regTotalPages ? 'not-allowed' : 'pointer',
                  border: '1px solid var(--border)', background: regPage >= regTotalPages ? '#f1f5f9' : '#ffffff',
                  color: regPage >= regTotalPages ? '#94a3b8' : '#153054', boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                }}>
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════ */}
      {/* TAB 2: FORMAT RULES & CONFIGURATION                      */}
      {/* ════════════════════════════════════════════════════════ */}
      {activeTab === 'config' && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))',
          gap: 24
        }}>
          {/* Format Settings Form */}
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border)',
            borderRadius: 16, padding: 24, boxShadow: '0 4px 20px -2px rgba(0,0,0,0.04)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <Settings size={18} color="#153054" />
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>Format Controls</h2>
            </div>

            <form onSubmit={handleSaveConfig} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Company Prefix */}
              <div>
                <label className="form-label" style={{ fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>
                  Company Prefix <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input className="form-control" maxLength={10}
                  placeholder="e.g. CQ, SIMS"
                  style={{
                    fontWeight: 800, letterSpacing: '0.05em', fontSize: '0.95rem',
                    border: '1.5px solid var(--border)', padding: '10px 14px', borderRadius: 8
                  }}
                  value={config.companyPrefix}
                  onChange={e => setConfig({ ...config, companyPrefix: e.target.value.toUpperCase() })} />
                <small style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4, display: 'block' }}>
                  Short prefix code for your company (e.g. <strong style={{ color: '#153054' }}>CQ</strong> for Carat Quest)
                </small>
              </div>

              {/* Chars per field */}
              <div>
                <label className="form-label" style={{ fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>Chars per Field</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[1,2,3,4].map(n => (
                    <button key={n} type="button"
                      onClick={() => setConfig({ ...config, charCount: n })}
                      style={{
                        flex: 1, padding: '9px 0', borderRadius: 8, fontSize: '0.88rem', fontWeight: 800,
                        cursor: 'pointer',
                        border: config.charCount === n ? '2px solid #0d1f35' : '1px solid var(--border)',
                        background: config.charCount === n ? 'linear-gradient(135deg, #0d1f35 0%, #1e3e66 100%)' : '#f8fafc',
                        color: config.charCount === n ? '#ffffff' : 'var(--text-secondary)',
                        boxShadow: config.charCount === n ? '0 3px 10px rgba(13,31,53,0.3)' : 'none',
                        transition: 'all 0.15s ease-in-out',
                      }}>{n}</button>
                  ))}
                </div>
                <small style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4, display: 'block' }}>
                  Characters taken from Item Name (e.g. <strong style={{ color: '#153054' }}>2</strong> → Ring = RG)
                </small>
              </div>

              {/* Sequence digits */}
              <div>
                <label className="form-label" style={{ fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>Sequence Digits</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[3,4,5,6].map(n => (
                    <button key={n} type="button"
                      onClick={() => setConfig({ ...config, seqPadding: n })}
                      style={{
                        flex: 1, padding: '9px 0', borderRadius: 8, fontSize: '0.88rem', fontWeight: 800,
                        cursor: 'pointer',
                        border: config.seqPadding === n ? '2px solid #0d1f35' : '1px solid var(--border)',
                        background: config.seqPadding === n ? 'linear-gradient(135deg, #0d1f35 0%, #1e3e66 100%)' : '#f8fafc',
                        color: config.seqPadding === n ? '#ffffff' : 'var(--text-secondary)',
                        boxShadow: config.seqPadding === n ? '0 3px 10px rgba(13,31,53,0.3)' : 'none',
                        transition: 'all 0.15s ease-in-out',
                      }}>{n}</button>
                  ))}
                </div>
                <small style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4, display: 'block' }}>
                  Zero-padded sequence length (e.g. <strong style={{ color: '#153054' }}>5</strong> → 00001)
                </small>
              </div>

              {/* Separator */}
              <div>
                <label className="form-label" style={{ fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>Separator Between Parts</label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {[{ v: '', l: 'None (CQRG12M00001)' }, { v: '-', l: 'Dash (CQ-RG-00001)' }, { v: '/', l: 'Slash (CQ/RG/00001)' }].map(s => (
                    <button key={`s-${s.v || 'n'}`} type="button"
                      onClick={() => setConfig({ ...config, separator: s.v })}
                      style={{
                        padding: '9px 12px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 800,
                        cursor: 'pointer', flex: 1,
                        border: config.separator === s.v ? '2px solid #0d1f35' : '1px solid var(--border)',
                        background: config.separator === s.v ? 'linear-gradient(135deg, #0d1f35 0%, #1e3e66 100%)' : '#f8fafc',
                        color: config.separator === s.v ? '#ffffff' : 'var(--text-secondary)',
                        boxShadow: config.separator === s.v ? '0 3px 10px rgba(13,31,53,0.3)' : 'none',
                        transition: 'all 0.15s ease-in-out',
                      }}>{s.l}</button>
                  ))}
                </div>
              </div>

              {/* Current Sequence & Reset Section */}
              <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                <label className="form-label" style={{ fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6, display: 'block' }}>
                  Counter Overrides & Reset
                </label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input type="number" min="0" className="form-control"
                    style={{ width: 130, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.95rem', padding: '7px 10px', borderRadius: 8 }}
                    value={config.currentSeq}
                    onChange={e => setConfig({ ...config, currentSeq: Math.max(0, parseInt(e.target.value) || 0) })} />
                  <button type="button" className="btn"
                    style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(239,68,68,0.25)' }}
                    onClick={handleResetToZero}>
                    <RotateCcw size={14} /> Reset Counter
                  </button>
                </div>
              </div>

              <button type="submit" disabled={saving}
                style={{
                  width: '100%', justifyContent: 'center', gap: 8, marginTop: 4, padding: '12px 0',
                  borderRadius: 10, fontSize: '0.92rem', fontWeight: 800,
                  background: 'linear-gradient(135deg, #0d1f35 0%, #1e3e66 100%)',
                  color: '#ffffff', border: 'none', cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(13,31,53,0.35)',
                  display: 'flex', alignItems: 'center'
                }}>
                <Save size={16} />
                {saving ? 'Saving Format...' : 'Save Format Settings'}
              </button>
            </form>
          </div>

          {/* SKU Format Guide */}
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border)',
            borderRadius: 16, padding: 24, boxShadow: '0 4px 20px -2px rgba(0,0,0,0.04)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Info size={16} color="#153054" />
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>SKU Format Breakdown</h2>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)', background: '#f8fafc' }}>
                    {['Part', 'Example', 'Generated Tag', 'Description'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 800 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Company Prefix', '"CQ"', 'CQ', 'Fixed company short code'],
                    ['Item Name', '"Ring"', 'RG', `First ${config.charCount} characters, uppercased`],
                    ['Size', '"14"', '14', 'Appears exactly as entered'],
                    ['Gender', 'Women', 'W', '1 char — W, M, or U'],
                    ['Auto Sequence', 'Auto', '00081', `${config.seqPadding}-digit, auto-increments`],
                  ].map(([part, input, inSku, desc], i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 800, color: 'var(--text-primary)' }}>{part}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{input}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <code style={{ background: '#f0f5fd', color: '#153054', border: '1px solid #d0e1f9', padding: '3px 8px', borderRadius: 6, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>{inSku}</code>
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{
              marginTop: 18, padding: '14px 18px', background: '#f0f5fd', border: '1px solid #d0e1f9',
              borderRadius: 12, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
            }}>
              <strong style={{ color: '#153054', fontWeight: 800 }}>Sample SKU Result: </strong>
              <span style={{ color: '#153054', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                CQ + RG + 14 + W + 00081 =
              </span>
              <span style={{
                background: 'linear-gradient(135deg, #0d1f35 0%, #1e3e66 100%)', color: '#f59e0b', padding: '4px 14px',
                borderRadius: 6, fontWeight: 900, fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: '0.04em', boxShadow: '0 2px 6px rgba(13,31,53,0.3)'
              }}>
                CQRG14W00081
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit SKU Modal ── */}
      {editModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(11,24,41,0.6)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20
        }}>
          <div style={{
            background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 440,
            padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)', border: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Edit2 size={18} color="#153054" /> Edit Line SKU
              </h3>
              <button onClick={() => setEditModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveEditedSKU} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ background: '#f8fafc', padding: 12, borderRadius: 10, border: '1px solid #e2e8f0', fontSize: '0.85rem' }}>
                <div><strong>Item:</strong> {editModal.itemName || editModal.ItemName || editModal.itemname}</div>
                <div><strong>Invoice:</strong> #{editModal.purchaseNo || editModal.PurchaseNo} ({editModal.supplierName || editModal.SupplierName})</div>
              </div>

              <div>
                <label className="form-label" style={{ fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>
                  SKU Number <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  style={{ fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", fontSize: '1rem', letterSpacing: '0.05em' }}
                  value={editSKUForm.skuNo}
                  onChange={e => setEditSKUForm({ ...editSKUForm, skuNo: e.target.value.toUpperCase() })}
                />
              </div>

              <div>
                <label className="form-label" style={{ fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>
                  Collection
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. WOMEN, MEN"
                  value={editSKUForm.collection}
                  onChange={e => setEditSKUForm({ ...editSKUForm, collection: e.target.value })}
                />
              </div>

              <div>
                <label className="form-label" style={{ fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>
                  Size
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. 14, 16"
                  value={editSKUForm.productSize}
                  onChange={e => setEditSKUForm({ ...editSKUForm, productSize: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setEditModal(null)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 8, fontWeight: 700,
                    border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', cursor: 'pointer'
                  }}>
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 8, fontWeight: 800,
                    border: 'none', background: 'linear-gradient(135deg, #0d1f35 0%, #1e3e66 100%)',
                    color: '#ffffff', cursor: 'pointer', boxShadow: '0 4px 12px rgba(13,31,53,0.3)'
                  }}>
                  {editSaving ? 'Saving...' : 'Save SKU'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
